import { useState } from 'react'
import { Form, Input, Button, Card, message, DatePicker, InputNumber, Select, Alert, Divider } from 'antd'
import { useNavigate } from 'react-router-dom'
import { assignmentApi } from '../../api/auth'
import { BulbOutlined } from '@ant-design/icons'

const { TextArea } = Input

// 智能题目模板
const questionTemplates: any = {
  formula: {
    sum: {
      title: 'SUM函数的使用',
      description: '学习使用SUM函数计算数值总和。\n\n要求:\n1. 在C列使用SUM函数计算A列和B列的总和\n2. 例如: C1 = SUM(A1:B1)\n3. 需要计算10行数据的总和',
      validation: 'formula',
      formula: 'SUM'
    },
    average: {
      title: 'AVERAGE函数的使用',
      description: '学习使用AVERAGE函数计算平均值。\n\n要求:\n1. 在D列使用AVERAGE函数计算A、B、C列的平均值\n2. 例如: D1 = AVERAGE(A1:C1)\n3. 需要计算10行数据的平均值',
      validation: 'formula',
      formula: 'AVERAGE'
    },
    max_min: {
      title: 'MAX和MIN函数的使用',
      description: '学习使用MAX和MIN函数找出最大值和最小值。\n\n要求:\n1. 在D列使用MAX函数找出A、B、C列的最大值\n2. 在E列使用MIN函数找出A、B、C列的最小值\n3. 需要处理10行数据',
      validation: 'formula',
      formula: 'MAX,MIN'
    },
    count: {
      title: 'COUNT函数的使用',
      description: '学习使用COUNT函数统计数值个数。\n\n要求:\n1. 使用COUNT函数统计A列中的数值个数\n2. 使用COUNTA函数统计B列中的非空单元格个数\n3. 结果放在D1和E1单元格',
      validation: 'formula',
      formula: 'COUNT'
    }
  },
  data_analysis: {
    sales: {
      title: '销售数据分析',
      description: '分析销售数据,计算关键指标。\n\n要求:\n1. 计算总销售额(SUM)\n2. 计算平均销售额(AVERAGE)\n3. 找出最高和最低销售额(MAX, MIN)\n4. 计算销售增长率',
      validation: 'value',
      formula: 'multiple'
    },
    grade: {
      title: '成绩数据分析',
      description: '分析学生成绩数据。\n\n要求:\n1. 计算每个学生的总分\n2. 计算平均分\n3. 判断是否及格(>=60分)\n4. 统计及格人数',
      validation: 'value',
      formula: 'multiple'
    }
  },
  chart: {
    bar: {
      title: '柱状图制作',
      description: '根据数据制作柱状图。\n\n要求:\n1. 选择A列和B列数据\n2. 插入柱状图\n3. 设置图表标题和坐标轴标签\n4. 调整图表样式',
      validation: 'chart',
      formula: 'none'
    }
  },
  comprehensive: {
    invoice: {
      title: '发票综合处理',
      description: '综合运用多种函数处理发票数据。\n\n要求:\n1. 计算每张发票的税额(金额×税率)\n2. 计算含税金额\n3. 统计总金额和总税额\n4. 标记大额发票(>10000)',
      validation: 'formula',
      formula: 'multiple'
    }
  }
}

export default function AssignmentCreate() {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  // 获取当前类型的模板选项
  const getTemplateOptions = () => {
    if (!selectedType) return []
    const templates = questionTemplates[selectedType] || {}
    return Object.keys(templates).map(key => ({
      value: key,
      label: templates[key].title
    }))
  }

  // 应用模板
  const applyTemplate = () => {
    if (!selectedType || !selectedTemplate) {
      message.warning('请先选择题目类型和模板')
      return
    }

    const template = questionTemplates[selectedType]?.[selectedTemplate]
    if (template) {
      form.setFieldsValue({
        title: template.title,
        description: template.description
      })
      message.success('模板已应用,可根据需要修改')
    }
  }

  const onFinish = async (values: any) => {
    setLoading(true)
    try {
      // 添加模板信息
      const template = questionTemplates[selectedType]?.[selectedTemplate]
      const submitData = {
        ...values,
        template: selectedTemplate,
        validation: template?.validation || 'value',
        formula: template?.formula || ''
      }

      await assignmentApi.create(submitData)
      message.success('题目创建成功')
      navigate('/assignments')
    } catch (error: any) {
      message.error(error.message || '创建失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card title="创建新题目">
      <Alert
        message="智能出题功能"
        description="选择题目类型和模板,系统将自动生成相关的题目描述和Excel模板,并支持公式验证!"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ totalScore: 100 }}
      >
        <Form.Item
          label="题目类型"
          name="type"
          rules={[{ required: true, message: '请选择题目类型' }]}
        >
          <Select
            placeholder="选择题目类型"
            onChange={(value) => {
              setSelectedType(value)
              setSelectedTemplate('')
            }}
          >
            <Select.Option value="formula">公式计算</Select.Option>
            <Select.Option value="data_analysis">数据分析</Select.Option>
            <Select.Option value="chart">图表制作</Select.Option>
            <Select.Option value="comprehensive">综合应用</Select.Option>
          </Select>
        </Form.Item>

        {selectedType && (
          <Form.Item label="题目模板(可选)">
            <Select
              placeholder="选择模板快速生成题目"
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              options={getTemplateOptions()}
              style={{ marginBottom: 8 }}
            />
            <Button
              type="dashed"
              icon={<BulbOutlined />}
              onClick={applyTemplate}
              disabled={!selectedTemplate}
            >
              应用模板
            </Button>
          </Form.Item>
        )}

        <Divider />

        <Form.Item
          label="题目标题"
          name="title"
          rules={[{ required: true, message: '请输入题目标题' }]}
        >
          <Input placeholder="例如: SUM函数的使用" />
        </Form.Item>

        <Form.Item
          label="题目描述"
          name="description"
          rules={[{ required: true, message: '请输入题目描述' }]}
        >
          <TextArea
            rows={6}
            placeholder="详细描述题目要求,包括:\n1. 需要使用的函数\n2. 计算步骤\n3. 结果位置"
          />
        </Form.Item>

        <Form.Item
          label="总分"
          name="totalScore"
          rules={[{ required: true, message: '请输入总分' }]}
        >
          <InputNumber min={1} max={1000} />
        </Form.Item>

        <Form.Item
          label="截止日期"
          name="deadline"
          rules={[{ required: true, message: '请选择截止日期' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} size="large">
            创建题目
          </Button>
        </Form.Item>
      </Form>
    </Card>
  )
}
